import { ActivatedChallengeEventType, AppEvent, CreateWebhookRequest, EmailCaptureEventType, EventTargetType, FeatureType, FormEventType, Webhook } from '../types';
import { LitElement, css, html } from 'lit';
import { createWebhook, deleteWebhook, getWebhooks, testWebhook, updateWebhook } from '../helpers/api/webhooks-api';
import { customElement, state } from 'lit/decorators';
import { getAppEvent, getEventType } from '../helpers/api/messages-api';

import { StateController } from '../helpers/state/state-management/state-controller';
import challengeData from '../helpers/state/challenge-data';
import featuresInfo from '../helpers/state/features-info';
import { getMainPage } from '../helpers/page-name';
import { openChatWindow } from '../helpers/intercom';
import organizationInfo from '../helpers/state/organization-info';
import topicData from '../helpers/state/topic-data';

@customElement('webhook-management-component')
export class WebhookManagementComponent extends LitElement {
    private readonly amplitude_callsite: string = 'Webhook Management Component';

    @state()
    webhooks: Webhook[] = [];

    @state()
    selectedWebhook?: Webhook;

    @state()
    isCreating: boolean = false;

    @state()
    isEditing: boolean = false;

    @state()
    isTesting: boolean = false;

    @state()
    isLoading: boolean = true;

    @state()
    currentWebhookForm: Partial<CreateWebhookRequest> = {
        name: '',
        url: '',
        event: { Form: FormEventType.SubmissionCreated },
        entity_type: EventTargetType.Form,
        is_active: true
    };

    private readonly bindOrganizationState = new StateController(this, organizationInfo);

    async connectedCallback() {
        super.connectedCallback();
        await this.loadWebhooks();
    }

    private async loadWebhooks() {
        if (!topicData.topic?.organization_id?.$oid) {
            return;
        }

        try {
            this.isLoading = true;
            this.webhooks = await getWebhooks(topicData.topic.organization_id.$oid);
        } catch (error) {
            getMainPage().errorToastElement?.showToast('Failed to load webhooks');
            console.error('Error loading webhooks:', error);
        } finally {
            this.isLoading = false;
        }
    }

    private openCreateDialog() {
        if(!featuresInfo.apiAccessEnabled) {
            this.dispatchEvent(new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.ApiAccess
                }
            }));
            return;
        }
        this.isCreating = true;
        this.currentWebhookForm = {
            name: '',
            url: '',
            event: { Form: FormEventType.SubmissionCreated },
            entity_type: EventTargetType.Form,
            is_active: true
        };
    }

    private openEditDialog(webhook: Webhook) {
        this.selectedWebhook = webhook;
        this.isEditing = true;
        this.currentWebhookForm = {
            name: webhook.name,
            url: webhook.url,
            event: webhook.event,
            entity_type: webhook.entity_type,
            entity_id: webhook.entity_id,
            is_active: webhook.is_active
        };
    }

    private closeDialogs() {
        this.isCreating = false;
        this.isEditing = false;
        this.selectedWebhook = undefined;
        this.currentWebhookForm = {};
    }

    private async saveWebhook() {
        if (!topicData.topic?.organization_id?.$oid) {
            getMainPage().errorToastElement?.showToast('Workspace not found');
            return;
        }

        try {
            const webhookRequest: CreateWebhookRequest = {
                name: this.currentWebhookForm.name ?? '',
                url: this.currentWebhookForm.url ?? '',
                event: this.currentWebhookForm.event ?? { Form: FormEventType.SubmissionCreated },
                entity_id:
                    this.currentWebhookForm.entity_type === EventTargetType.Form || this.currentWebhookForm.entity_type === EventTargetType.EmailCapture ? { $oid: topicData.topicId } :
                        this.currentWebhookForm.entity_id ?? { $oid: challengeData.challenge?._id.$oid ?? '' },
                entity_type: this.currentWebhookForm.entity_type ?? EventTargetType.Form,
                is_active: this.currentWebhookForm.is_active ?? true
            };

            if (this.isEditing && this.selectedWebhook?._id?.$oid) {
                await updateWebhook(
                    this.amplitude_callsite,
                    topicData.topic.organization_id.$oid,
                    this.selectedWebhook._id.$oid,
                    webhookRequest
                );
                getMainPage().errorToastElement?.showToast('Webhook updated successfully');
            } else {
                await createWebhook(
                    this.amplitude_callsite,
                    topicData.topic.organization_id.$oid,
                    webhookRequest
                );
                getMainPage().errorToastElement?.showToast('Webhook created successfully');
            }

            await this.loadWebhooks();
            this.closeDialogs();
        } catch (error) {
            getMainPage().errorToastElement?.showToast(`Failed to ${this.isEditing ? 'update' : 'create'} webhook`);
            console.error('Error saving webhook:', error);
        }
    }

    private async deleteWebhook(webhook: Webhook) {
        if (!topicData.topic?.organization_id?.$oid || !webhook._id?.$oid) {
            return;
        }

        if (!confirm(`Are you sure you want to delete the webhook "${webhook.name}"?`)) {
            return;
        }

        try {
            await deleteWebhook(
                this.amplitude_callsite,
                topicData.topic.organization_id.$oid,
                webhook._id.$oid
            );
            getMainPage().errorToastElement?.showToast('Webhook deleted successfully');
            await this.loadWebhooks();
        } catch (error) {
            getMainPage().errorToastElement?.showToast('Failed to delete webhook');
            console.error('Error deleting webhook:', error);
        }
    }

    private async testWebhook(webhook: Webhook) {
        if (!topicData.topic?.organization_id?.$oid || !webhook._id?.$oid) {
            return;
        }

        try {
            this.isTesting = true;
            await testWebhook(
                webhook._id.$oid,
                topicData.topic.organization_id.$oid
            );
            getMainPage().errorToastElement?.showToast('Test webhook sent successfully');
        } catch (error) {
            getMainPage().errorToastElement?.showToast(`Failed to send test webhook. You may need to add data (like a registration or email submission) to trigger the test event.`);
            console.error('Error testing webhook:', error);
        } finally {
            this.isTesting = false;
        }
    }

    get relevantEvents(): AppEvent[] {
        switch (this.currentWebhookForm.entity_type) {
            case EventTargetType.Form:
                return [
                    {Form: FormEventType.SubmissionCreated},
                    {Form: FormEventType.SubmissionUpdated},
                    {Form: FormEventType.SubmissionDeleted}
                ];
            case EventTargetType.ActivatedChallenge:
                return [
                    {ActivatedChallenge: ActivatedChallengeEventType.LocationCheckin},
                    {ActivatedChallenge: ActivatedChallengeEventType.RewardEarned},
                    {ActivatedChallenge: ActivatedChallengeEventType.Registration}
                ];
            case EventTargetType.EmailCapture:
                return [
                    {EmailCapture: EmailCaptureEventType.GateCapture},
                    {EmailCapture: EmailCaptureEventType.PointInquiryCapture},
                    {EmailCapture: EmailCaptureEventType.PopUpCapture}
                ];
            default:
                return [];
        }
    }


    private getEntityTypeDisplayName(entityType: EventTargetType): string {
        switch (entityType) {
            case EventTargetType.Form:
                return 'Form/Map';
            case EventTargetType.ActivatedChallenge:
                return 'Challenge';
            case EventTargetType.EmailCapture:
                return 'Email Capture';
            default:
                return entityType;
        }
    }

    protected render() {
        if (this.isLoading) {
            return html`<loading-element loadingString="Loading webhooks..."></loading-element>`;
        }

        return html`
            <div class="webhook-management">
                <settings-card header="Webhook Management (beta)">
                    <form-button
                        slot="header"
                        size="sm"
                        outlined
                        @click=${this.openCreateDialog}
                    >
                        <mwc-icon slot="icon">add</mwc-icon>
                        Add Webhook
                    </form-button>
                    ${this.webhooks.length === 0 ? html`
                        <div class="empty-state">
                            <mwc-icon class="empty-icon">webhook</mwc-icon>
                            <h4>No webhooks configured</h4>
                            <p>Create your first webhook to start receiving real-time notifications</p>
                        </div>
                    ` : html`
                        ${this.webhooks.map(webhook => html`
                            <div class="webhook-item">
                                <div class="webhook-info">
                                    <div class="webhook-header">
                                        <h4>${webhook.name}</h4>
                                        <div class="webhook-status ${webhook.is_active ? 'active' : 'inactive'}">
                                            ${webhook.is_active ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                    <p class="webhook-url">${webhook.url}</p>
                                    <div class="webhook-details">
                                        <span class="detail-item">
                                            <strong>Event:</strong> ${getEventType(webhook.event)}
                                        </span>
                                    </div>
                                </div>
                                <div class="webhook-actions">
                                    <form-button
                                        variant="outline"
                                        size="sm"
                                        @click=${async () => await this.testWebhook(webhook)}
                                        ?disabled=${this.isTesting}
                                    >
                                        <mwc-icon slot="icon">send</mwc-icon>
                                        Test
                                    </form-button>
                                    <form-button
                                        variant="outline"
                                        size="sm"
                                        @click=${() => this.openEditDialog(webhook)}
                                    >
                                        <mwc-icon slot="icon">edit</mwc-icon>
                                        Edit
                                    </form-button>
                                    <form-button
                                        variant="outline"
                                        size="sm"
                                        class="delete-button"
                                        @click=${async () => await this.deleteWebhook(webhook)}
                                    >
                                        <mwc-icon slot="icon">delete</mwc-icon>
                                        Delete
                                    </form-button>
                                </div>
                            </div>
                        `)}
                    `}
                </settings-card>

                <settings-card header="How Webhooks Work">
                    <p>Webhooks allow you to receive real-time notifications when events occur in your organization. When configured, we'll send HTTP POST requests to your specified URLs containing event data.</p>

                    <h4>Event Types Available:</h4>
                    <ul>
                        <li><strong>Form Submission Created:</strong> Triggered when a new point is submitted to your map</li>
                        <li><strong>Form Submission Updated:</strong> Triggered when an existing point is modified</li>
                        <li><strong>Form Submission Deleted:</strong> Triggered when a point is deleted</li>
                        <li><strong>Challenge Check-in:</strong> Triggered when a participant checks into a challenge location</li>
                        <li><strong>Challenge Reward Earned:</strong> Triggered when a participant earns a reward</li>
                        <li> You may request additional event types by contacting support.</li>
                    </ul>

                    <div class="help-links">
                        <form-button
                            variant="outlined"
                            size="xs"
                            @click="${() => window.open('https://proximaps.notion.site/webhook-docs', '_blank')}"
                            icon="description"
                        >
                            View Documentation
                        </form-button>
                        <form-button
                            variant="outlined"
                            size="xs"
                            @click="${() => openChatWindow(this.amplitude_callsite, 'I need support with webhooks')}"
                            icon="support_agent"
                        >
                            Contact Tech Support
                        </form-button>
                    </div>
                </settings-card>

                <!-- Create/Edit Dialog -->
                ${this.isCreating || this.isEditing ? html`
                    <div class="dialog-overlay" @click=${this.closeDialogs}>
                        <div class="dialog" @click=${(e: Event) => e.stopPropagation()}>
                            <div class="dialog-header">
                                <h3>${this.isEditing ? 'Edit Webhook' : 'Create Webhook'}</h3>
                                <mwc-icon-button
                                    icon="close"
                                    @click=${this.closeDialogs}
                                ></mwc-icon-button>
                            </div>
                            <div class="dialog-content">
                                <proxi-textfield
                                    label="Webhook Name"
                                    outlined
                                    .value=${this.currentWebhookForm.name}
                                    @input=${(e: CustomEvent) => {this.currentWebhookForm.name = (e.target as HTMLInputElement).value;}}
                                    required
                                ></proxi-textfield>

                                <proxi-textfield
                                    label="Webhook URL"
                                    outlined
                                    .value=${this.currentWebhookForm.url}
                                    @input=${(e: CustomEvent) => {this.currentWebhookForm.url = (e.target as HTMLInputElement).value;}}
                                    placeholder="https://your-server.com/webhook"
                                    required
                                ></proxi-textfield>

                                <proxi-selectfield
                                    outlined
                                    label="Target Entity Type"
                                    .value=${this.currentWebhookForm.entity_type}
                                    @selected=${(e: CustomEvent) => {this.currentWebhookForm.entity_type = (
                                        e.target as HTMLInputElement).value === 'form' ? EventTargetType.Form :
                                            (e.target as HTMLInputElement).value === 'activated_challenge' ? EventTargetType.ActivatedChallenge : EventTargetType.EmailCapture;
                                            this.requestUpdate();}}
                                    required
                                >
                                    <mwc-list-item value="form">Form/Map Points</mwc-list-item>
                                    <mwc-list-item value="activated_challenge">Challenge/Passport Events</mwc-list-item>
                                    <mwc-list-item value="email_capture">Email Capture Events</mwc-list-item>
                                </proxi-selectfield>

                                ${this.currentWebhookForm.entity_type === EventTargetType.ActivatedChallenge ? html`
                                    <proxi-selectfield
                                        label="Challenge Name"
                                        outlined
                                        .value=${this.currentWebhookForm.entity_id?.$oid ?? challengeData.challenge?._id.$oid}
                                        @selected=${(e: CustomEvent) => {this.currentWebhookForm.entity_id = { $oid: (e.target as HTMLInputElement).value }; this.requestUpdate();}}
                                        required
                                    >
                                        ${topicData.allChallenges.map(challenge => html`
                                            <mwc-list-item value="${challenge._id.$oid}">${challenge.name}</mwc-list-item>
                                        `)}
                                    </proxi-selectfield>
                                ` : ''}

                                <proxi-selectfield
                                    label="Event Type"
                                    outlined
                                    .value=${getEventType(this.currentWebhookForm.event)}
                                    @selected=${(e: CustomEvent) => {this.currentWebhookForm.event = getAppEvent((e.target as HTMLInputElement).value); this.requestUpdate();}}
                                    required
                                >
                                    ${this.relevantEvents.map(event => html`
                                        <mwc-list-item value="${getEventType(event)}">${getEventType(event)}</mwc-list-item>
                                    `)}
                                </proxi-selectfield>
                                <div class="checkbox-wrapper">
                                    <form-checkbox
                                        .checked=${this.currentWebhookForm.is_active ?? true}
                                        @change=${(e: CustomEvent) => {this.currentWebhookForm.is_active = (e.target as HTMLInputElement).checked;}}
                                    ></form-checkbox>
                                    <label>Active</label>
                                </div>
                            </div>
                            <div class="dialog-actions">
                                <form-button
                                    variant="text"
                                    @click=${this.closeDialogs}
                                >
                                    Cancel
                                </form-button>
                                <form-button
                                    variant="filled"
                                    @click=${this.saveWebhook}
                                >
                                    ${this.isEditing ? 'Update' : 'Create'}
                                </form-button>
                            </div>
                        </div>
                    </div>
                ` : ''}
            </div>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .webhook-management {
                padding: 20px;
                max-width: 1200px;
                margin: 0 auto;
                gap: 10px;
                display: flex;
                flex-direction: column;
            }

            .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 24px;
            }

            .header h3 {
                margin: 0;
                color: var(--main-primary-color);
            }

            p {
                font-size: 0.8rem;
            }

            .help-links {
                margin-top: 16px;
                display: flex;
                gap: 10px;
            }

            .webhooks-list {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .empty-state {
                text-align: center;
                padding: 40px;
                color: var(--theme-input-color);
            }

            .empty-icon {
                --mdc-icon-size: 48px;
                margin-bottom: 16px;
                opacity: 0.5;
            }

            .empty-state h4 {
                margin: 0 0 8px 0;
                color: var(--main-primary-color);
            }

            .webhook-item {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 20px;
                padding: 5px;
                margin: 5px;
                border: 1px solid var(--theme-border-color);
            }

            .webhook-info {
                flex: 1;
            }

            .webhook-header {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 8px;
            }

            .webhook-header h4 {
                margin: 0;
                color: var(--main-primary-color);
            }

            .webhook-status {
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 0.75rem;
                font-weight: 600;
                text-transform: uppercase;
            }

            .webhook-status.active {
                background-color: var(--main-success-color);
                color: white;
            }

            .webhook-status.inactive {
                background-color: var(--theme-input-color);
                color: white;
            }

            .webhook-url {
                margin: 0 0 12px 0;
                font-family: monospace;
                font-size: 0.9rem;
                color: var(--theme-input-color);
                word-break: break-all;
            }

            .webhook-details {
                display: flex;
                flex-wrap: wrap;
                gap: 16px;
                font-size: 0.9rem;
            }

            .detail-item {
                color: var(--theme-input-color);
            }

            .webhook-actions {
                display: flex;
                gap: 8px;
                flex-shrink: 0;
            }

            .delete-button {
                --mdc-theme-primary: var(--main-invalid-color);
            }

            .dialog-overlay {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.5);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 1000;
            }

            .dialog {
                background: var(--theme-white-color);
                border-radius: 8px;
                width: 90%;
                max-width: 500px;
                max-height: 90vh;
                overflow: auto;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
            }

            .dialog-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 20px 20px 0 20px;
                border-bottom: 1px solid var(--theme-border-color);
            }

            .dialog-header h3 {
                margin: 0;
                color: var(--main-primary-color);
            }

            .dialog-content {
                padding: 20px;
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .dialog-actions {
                padding: 0 20px 20px 20px;
                display: flex;
                justify-content: flex-end;
                gap: 12px;
            }

            .checkbox-wrapper {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .checkbox-wrapper label {
                font-size: 0.9rem;
                color: var(--theme-input-color);
            }

            @media (max-width: 768px) {
                .webhook-management {
                    padding: 16px;
                }

                .header {
                    flex-direction: column;
                    align-items: stretch;
                    gap: 16px;
                }

                .webhook-item {
                    flex-direction: column;
                    gap: 16px;
                }

                .webhook-actions {
                    justify-content: flex-start;
                }

                .webhook-details {
                    flex-direction: column;
                    gap: 8px;
                }

                .dialog {
                    width: 95%;
                    margin: 20px;
                }
            }
        `;
    }
}